
import React from 'react';
import { TrackDownload } from '@/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Music, Download, TrendingUp } from 'lucide-react';

interface StatsSummaryProps {
  uploads: TrackDownload[];
  timeRange: string;
}

const StatsSummary: React.FC<StatsSummaryProps> = ({ uploads, timeRange }) => {
  // Calculate summary statistics
  const totalTracks = uploads.length;
  const totalDownloads = uploads.reduce((sum, track) => sum + track.downloadCount, 0);
  
  // Calculate most downloaded track
  const mostDownloadedTrack = uploads.length > 0 
    ? uploads.reduce((most, track) => {
        return track.downloadCount > most.downloadCount ? track : most;
      }, uploads[0])
    : null;
  
  // Find newest track release date
  const newestTrack = uploads.length > 0 
    ? uploads.reduce((newest, track) => {
        return new Date(track.createdAt) > new Date(newest.createdAt) ? track : newest;
      }, uploads[0])
    : null;
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="overflow-hidden border-border hover:border-primary/50 transition-colors">
        <CardContent className="pt-6 px-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1.5">Total Tracks</p>
              <h3 className="text-2xl font-bold">{totalTracks}</h3>
            </div>
            <div className="bg-primary/10 p-2 rounded-full">
              <Music className="h-5 w-5 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="overflow-hidden border-border hover:border-primary/50 transition-colors">
        <CardContent className="pt-6 px-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1.5">Total Downloads</p>
              <h3 className="text-2xl font-bold">{totalDownloads}</h3>
            </div>
            <div className="bg-green-500/10 p-2 rounded-full">
              <Download className="h-5 w-5 text-green-500" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="overflow-hidden border-border hover:border-primary/50 transition-colors">
        <CardContent className="pt-6 px-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1.5">Top Track</p>
              {mostDownloadedTrack ? (
                <>
                  <h3 className="text-lg font-bold truncate max-w-[160px]">
                    {mostDownloadedTrack.trackName || mostDownloadedTrack.fileName.split('.')[0]}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {mostDownloadedTrack.downloadCount} downloads
                  </p>
                </>
              ) : (
                <h3 className="text-lg font-bold">N/A</h3>
              )}
            </div>
            <div className="bg-purple-500/10 p-2 rounded-full">
              <TrendingUp className="h-5 w-5 text-purple-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatsSummary;
